import React, { useState, useEffect, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5001`;

const CATEGORY_INFO = {
  gas: { label: 'Gases', color: '#4ecca3', icon: '🔥' },
  liquid: { label: 'Liquids', color: '#3B82F6', icon: '🧪' },
  steam: { label: 'Steam', color: '#9CA3AF', icon: '☁' },
  mixture: { label: 'Mixtures / Syngas', color: '#F97316', icon: '🔄' },
  utility: { label: 'Utilities & Power', color: '#F5C542', icon: '⚡' },
};

/** Calculate mixture properties from composition + pure gas data */
function calcMixtureProps(composition, allMedia) {
  if (!composition || composition.length === 0) return {};
  let totalMolPct = composition.reduce((s, c) => s + (c.mol_pct || 0), 0);
  if (totalMolPct === 0) totalMolPct = 100;

  let molWeight = 0, density = 0, lhvKg = 0, lhvM3 = 0, hhvKg = 0, hhvM3 = 0;
  for (const c of composition) {
    const frac = (c.mol_pct || 0) / totalMolPct;
    const pure = allMedia.find(m => m.code === c.media_code);
    if (!pure) continue;
    molWeight += frac * (pure.mol_weight || 0);
    density += frac * (pure.density_kg_m3 || 0);
    lhvM3 += frac * (pure.lhv_mj_per_m3 || 0);
    hhvM3 += frac * (pure.hhv_mj_per_m3 || 0);
  }
  // LHV per kg from volumetric: lhv_kg = lhv_m3 / density
  if (density > 0) {
    lhvKg = lhvM3 / density;
    hhvKg = hhvM3 / density;
  }
  return {
    mol_weight: Math.round(molWeight * 100) / 100,
    density_kg_m3: Math.round(density * 1000) / 1000,
    lhv_mj_per_kg: Math.round(lhvKg * 100) / 100 || null,
    hhv_mj_per_kg: Math.round(hhvKg * 100) / 100 || null,
    lhv_mj_per_m3: Math.round(lhvM3 * 100) / 100 || null,
    hhv_mj_per_m3: Math.round(hhvM3 * 100) / 100 || null,
    total_mol_pct: Math.round(totalMolPct * 100) / 100,
  };
}

const ProcessMediaView = () => {
  const [media, setMedia] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [loading, setLoading] = useState(true);

  // Mixture editor
  const [editingMixture, setEditingMixture] = useState(null); // full editable copy
  const [pureGases, setPureGases] = useState([]);
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [mRes, cRes] = await Promise.all([
        fetch(`${API}/api/process-media`),
        fetch(`${API}/api/process-media/categories`),
      ]);
      if (mRes.ok) setMedia(await mRes.json());
      if (cRes.ok) setCategories(await cRes.json());
    } catch {}
    setLoading(false);
  }, []);

  const fetchGases = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/process-media/gases`);
      if (res.ok) setPureGases(await res.json());
    } catch {}
  }, []);

  useEffect(() => { fetchAll(); fetchGases(); }, [fetchAll, fetchGases]);

  const filtered = activeCategory ? media.filter(m => m.category === activeCategory) : media;

  // --- Mixture editor handlers ---
  const startEditMixture = (m) => {
    setEditingMixture(JSON.parse(JSON.stringify(m)));
    setSelectedMedia(null);
  };

  const startCopyMixture = (m) => {
    const copy = JSON.parse(JSON.stringify(m));
    copy.id = null; // new entry
    copy.code = copy.code + '_COPY';
    copy.name = copy.name + ' (Copy)';
    copy.source = 'custom';
    setEditingMixture(copy);
    setSelectedMedia(null);
  };

  const startNewMixture = () => {
    setEditingMixture({
      id: null, code: '', name: '', formula: '', category: 'mixture',
      phase_at_stp: 'gas', is_mixture: true, composition: [],
      mol_weight: null, density_kg_m3: null, lhv_mj_per_kg: null,
      color: '#F97316', icon: 'SG', pypsa_carrier: '', pypsa_bus_type: 'gas',
      flammable: true, toxic: false, corrosive: false, source: 'custom', notes: '',
    });
  };

  const updateMixComp = (idx, field, value) => {
    setEditingMixture(prev => {
      const comp = [...prev.composition];
      comp[idx] = { ...comp[idx], [field]: field === 'mol_pct' ? parseFloat(value) || 0 : value };
      return { ...prev, composition: comp };
    });
  };

  const addMixComponent = () => {
    setEditingMixture(prev => ({
      ...prev,
      composition: [...prev.composition, { media_code: '', mol_pct: 0 }],
    }));
  };

  const removeMixComponent = (idx) => {
    setEditingMixture(prev => ({
      ...prev,
      composition: prev.composition.filter((_, i) => i !== idx),
    }));
  };

  const recalcMixture = () => {
    if (!editingMixture) return;
    const props = calcMixtureProps(editingMixture.composition, media);
    setEditingMixture(prev => ({ ...prev, ...props, density_note: 'calculated from composition at STP' }));
  };

  const saveMixture = async () => {
    if (!editingMixture) return;
    setSaving(true);
    try {
      const isNew = !editingMixture.id;
      const url = isNew ? `${API}/api/process-media` : `${API}/api/process-media/${editingMixture.id}`;
      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editingMixture),
      });
      if (res.ok) {
        setEditingMixture(null);
        fetchAll();
      } else {
        const err = await res.json();
        alert(err.error || 'Save failed');
      }
    } catch (e) { alert(e.message); }
    setSaving(false);
  };

  // --- Mixture editor view ---
  if (editingMixture) {
    const m = editingMixture;
    const isNew = !m.id;
    const props = calcMixtureProps(m.composition, media);
    const totalPct = props.total_mol_pct || 0;
    const pctValid = Math.abs(totalPct - 100) < 0.1;

    return (
      <div className="def-editor">
        <div className="def-editor-header">
          <div className="def-editor-title">
            <div style={{ width: 36, height: 36, borderRadius: '50%', background: m.color || '#F97316', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, border: '2px solid rgba(255,255,255,0.2)' }}>
              {m.icon || '🔄'}
            </div>
            <div>
              <h2>{isNew ? 'New Gas Mixture' : `Edit: ${m.name}`}</h2>
              <span className="def-editor-id">{m.code || '...'}</span>
            </div>
          </div>
          <div className="def-editor-actions">
            <button className="btn-ghost" onClick={() => setEditingMixture(null)}>{'← Back'}</button>
            <button className="btn-accent" onClick={saveMixture} disabled={saving || !m.code || !m.name || !pctValid}>
              {saving ? 'Saving...' : isNew ? 'Create Mixture' : 'Save Changes'}
            </button>
          </div>
        </div>

        <div className="def-editor-body">
          <div className="def-editor-left">
            {/* Basic info */}
            <div className="def-section">
              <h3>Mixture Properties</h3>
              <div className="def-form-row">
                <div className="def-form-group"><label>Code *</label><input value={m.code} onChange={e => setEditingMixture(prev => ({ ...prev, code: e.target.value.toUpperCase().replace(/\s+/g, '_') }))} placeholder="SYNGAS_BIO" /></div>
                <div className="def-form-group"><label>Name *</label><input value={m.name} onChange={e => setEditingMixture(prev => ({ ...prev, name: e.target.value }))} placeholder="Biogas" /></div>
              </div>
              <div className="def-form-row">
                <div className="def-form-group"><label>Formula</label><input value={m.formula || ''} onChange={e => setEditingMixture(prev => ({ ...prev, formula: e.target.value }))} placeholder="CH4+CO2" /></div>
                <div className="def-form-group"><label>Color</label><div className="color-input-wrap"><input type="color" value={m.color || '#F97316'} onChange={e => setEditingMixture(prev => ({ ...prev, color: e.target.value }))} className="color-picker" /><input type="text" value={m.color || ''} onChange={e => setEditingMixture(prev => ({ ...prev, color: e.target.value }))} className="color-hex mono" /></div></div>
              </div>
              <div className="def-form-row">
                <div className="def-form-group"><label>Notes</label><input value={m.notes || ''} onChange={e => setEditingMixture(prev => ({ ...prev, notes: e.target.value }))} placeholder="Source, application..." /></div>
              </div>
            </div>

            {/* Composition table */}
            <div className="def-section">
              <div className="def-section-header">
                <h3>Composition (mol%)</h3>
                <button className="btn-sm-accent" onClick={addMixComponent}>+ Add Component</button>
              </div>
              <p className="form-hint" style={{ marginBottom: 8 }}>
                For ideal gases at STP, mol% = vol% (Avogadro's law). Total must equal 100%.
              </p>

              <div className="conductor-table-wrap">
                <table className="conductor-table">
                  <thead>
                    <tr><th>Component</th><th>mol%</th><th style={{ width: 60 }}>MW</th><th style={{ width: 80 }}>LHV (MJ/kg)</th><th></th></tr>
                  </thead>
                  <tbody>
                    {m.composition.map((c, i) => {
                      const pure = media.find(x => x.code === c.media_code) || pureGases.find(x => x.code === c.media_code);
                      return (
                        <tr key={i}>
                          <td>
                            <select value={c.media_code} onChange={e => updateMixComp(i, 'media_code', e.target.value)} style={{ width: '100%' }}>
                              <option value="">-- select --</option>
                              {pureGases.map(g => <option key={g.code} value={g.code}>{g.name} ({g.code})</option>)}
                            </select>
                          </td>
                          <td><input type="number" step="0.1" min="0" max="100" value={c.mol_pct} onChange={e => updateMixComp(i, 'mol_pct', e.target.value)} className="mono" style={{ width: 70 }} /></td>
                          <td className="mono" style={{ color: 'var(--text-muted)' }}>{pure?.mol_weight?.toFixed(1) || '—'}</td>
                          <td className="mono" style={{ color: 'var(--text-muted)' }}>{pure?.lhv_mj_per_kg || '—'}</td>
                          <td><button className="btn-remove-field" onClick={() => removeMixComponent(i)}>&times;</button></td>
                        </tr>
                      );
                    })}
                    {/* Total row */}
                    <tr style={{ borderTop: '2px solid var(--cenec-dark-surface)' }}>
                      <td style={{ fontWeight: 600 }}>TOTAL</td>
                      <td className="mono" style={{ fontWeight: 700, color: pctValid ? 'var(--cenec-green)' : 'var(--danger)' }}>{totalPct.toFixed(1)}%</td>
                      <td className="mono" style={{ fontWeight: 600 }}>{props.mol_weight}</td>
                      <td className="mono" style={{ fontWeight: 600 }}>{props.lhv_mj_per_kg || '—'}</td>
                      <td></td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {!pctValid && <div className="download-msg error" style={{ marginTop: 8 }}>Total must equal 100% (currently {totalPct.toFixed(1)}%)</div>}

              <button className="btn-ghost" onClick={recalcMixture} style={{ marginTop: 8 }}>Recalculate Properties from Composition</button>
            </div>
          </div>

          {/* Right: Calculated properties */}
          <div className="def-editor-right">
            <div className="def-section">
              <h3>Calculated Properties</h3>
              <div className="conductor-props-grid">
                <PropRow label="Molecular Weight" value={props.mol_weight} unit="g/mol" highlight />
                <PropRow label="Density (STP)" value={props.density_kg_m3} unit="kg/m³" />
                {props.lhv_mj_per_kg && <PropRow label="LHV" value={props.lhv_mj_per_kg} unit="MJ/kg" />}
                {props.hhv_mj_per_kg && <PropRow label="HHV" value={props.hhv_mj_per_kg} unit="MJ/kg" />}
                {props.lhv_mj_per_m3 && <PropRow label="LHV (volumetric)" value={props.lhv_mj_per_m3} unit="MJ/Nm³" highlight />}
                {props.hhv_mj_per_m3 && <PropRow label="HHV (volumetric)" value={props.hhv_mj_per_m3} unit="MJ/Nm³" />}
                {props.lhv_mj_per_kg && <PropRow label="LHV" value={(props.lhv_mj_per_kg / 3.6).toFixed(2)} unit="kWh/kg" />}
              </div>
              <div style={{ marginTop: 8, fontSize: 10, color: 'var(--text-muted)' }}>
                Note: For ideal gases at STP (0°C, 101.325 kPa), <b>mol% = vol%</b> by Avogadro's law.
                Properties are linear mixing of pure component data.
              </div>
            </div>

            <div className="def-section">
              <h3>PyPSA Mapping</h3>
              <div className="def-form-row">
                <div className="def-form-group"><label>Carrier</label><input value={m.pypsa_carrier || ''} onChange={e => setEditingMixture(prev => ({ ...prev, pypsa_carrier: e.target.value }))} placeholder="syngas" /></div>
                <div className="def-form-group"><label>Bus Type</label><input value={m.pypsa_bus_type || ''} onChange={e => setEditingMixture(prev => ({ ...prev, pypsa_bus_type: e.target.value }))} placeholder="gas" /></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Detail view
  if (selectedMedia) {
    const m = selectedMedia;
    const catInfo = CATEGORY_INFO[m.category] || {};
    return (
      <div className="def-editor">
        <div className="def-editor-header">
          <div className="def-editor-title">
            <div style={{ width: 36, height: 36, borderRadius: '50%', background: m.color || catInfo.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, border: '2px solid rgba(255,255,255,0.2)' }}>
              {m.icon || catInfo.icon}
            </div>
            <div>
              <h2>{m.name}</h2>
              <span className="def-editor-id">{m.code} · {m.formula || '—'} · {catInfo.label}</span>
            </div>
          </div>
          <div className="def-editor-actions">
            <button className="btn-ghost" onClick={() => setSelectedMedia(null)}>{'← Back'}</button>
            {m.is_mixture && (
              <>
                <button className="btn-ghost" onClick={() => startEditMixture(m)}>Edit Composition</button>
                <button className="btn-accent" onClick={() => startCopyMixture(m)}>Copy & Modify</button>
              </>
            )}
          </div>
        </div>
        <div className="def-editor-body">
          <div className="def-editor-left">
            {/* Physical properties */}
            <div className="def-section">
              <h3>Physical Properties</h3>
              <div className="conductor-props-grid">
                {m.mol_weight && <PropRow label="Molecular Weight" value={m.mol_weight} unit="g/mol" />}
                {m.density_kg_m3 && <PropRow label={`Density ${m.density_note ? `(${m.density_note})` : ''}`} value={m.density_kg_m3} unit="kg/m³" />}
                {m.boiling_point_c != null && <PropRow label="Boiling Point" value={m.boiling_point_c} unit="°C" />}
                {m.melting_point_c != null && <PropRow label="Melting Point" value={m.melting_point_c} unit="°C" />}
                {m.critical_temp_c != null && <PropRow label="Critical Temp" value={m.critical_temp_c} unit="°C" />}
                {m.critical_pressure_bar != null && <PropRow label="Critical Pressure" value={m.critical_pressure_bar} unit="bar" />}
                {m.specific_heat_kj_kgk && <PropRow label="Specific Heat (Cp)" value={m.specific_heat_kj_kgk} unit="kJ/(kg·K)" />}
              </div>
            </div>

            {/* Energy content */}
            {(m.lhv_mj_per_kg || m.hhv_mj_per_kg) && (
              <div className="def-section">
                <h3>Energy Content</h3>
                <div className="conductor-props-grid">
                  {m.lhv_mj_per_kg && <PropRow label="LHV" value={m.lhv_mj_per_kg} unit="MJ/kg" highlight />}
                  {m.hhv_mj_per_kg && <PropRow label="HHV" value={m.hhv_mj_per_kg} unit="MJ/kg" />}
                  {m.lhv_mj_per_m3 && <PropRow label="LHV (volumetric)" value={m.lhv_mj_per_m3} unit="MJ/Nm³" />}
                  {m.hhv_mj_per_m3 && <PropRow label="HHV (volumetric)" value={m.hhv_mj_per_m3} unit="MJ/Nm³" />}
                  {m.lhv_mj_per_kg && <PropRow label="LHV" value={(m.lhv_mj_per_kg / 3.6).toFixed(2)} unit="kWh/kg" />}
                </div>
              </div>
            )}

            {/* Composition (mixtures) */}
            {m.is_mixture && m.composition && (
              <div className="def-section">
                <h3>Composition</h3>
                <div className="conductor-table-wrap">
                  <table className="conductor-table">
                    <thead><tr><th>Component</th><th>Mol %</th></tr></thead>
                    <tbody>
                      {m.composition.map((c, i) => {
                        const comp = media.find(x => x.code === c.media_code);
                        return (
                          <tr key={i}>
                            <td>{comp?.name || c.media_code} ({c.media_code})</td>
                            <td className="mono" style={{ fontWeight: 600 }}>{c.mol_pct}%</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {m.mol_weight && (
                  <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-muted)' }}>
                    Calculated mol weight: {m.mol_weight} g/mol · Density: {m.density_kg_m3} kg/m³
                  </div>
                )}
              </div>
            )}

            {/* Steam specific */}
            {m.category === 'steam' && (
              <div className="def-section">
                <h3>Steam Properties</h3>
                <div className="conductor-props-grid">
                  <PropRow label="Pressure" value={m.steam_pressure_bar} unit="bar" highlight />
                  <PropRow label="Temperature" value={m.steam_temp_c} unit="°C" />
                  <PropRow label="Enthalpy" value={m.steam_enthalpy_kj_kg} unit="kJ/kg" />
                </div>
              </div>
            )}
          </div>

          <div className="def-editor-right">
            {/* Safety */}
            <div className="def-section">
              <h3>Safety</h3>
              <div className="conductor-props-grid">
                <PropRow label="Flammable" value={m.flammable ? 'Yes' : 'No'} />
                <PropRow label="Toxic" value={m.toxic ? 'Yes' : 'No'} />
                <PropRow label="Corrosive" value={m.corrosive ? 'Yes' : 'No'} />
                {m.explosion_limits && <PropRow label="Explosion Limits" value={m.explosion_limits} />}
              </div>
            </div>

            {/* PyPSA */}
            {m.pypsa_carrier && (
              <div className="pypsa-box">
                <h3>PyPSA / Pyomo Mapping</h3>
                <div className="pypsa-params">
                  <div className="pypsa-param"><span className="pypsa-key">carrier</span><span className="pypsa-val">{m.pypsa_carrier}</span></div>
                  <div className="pypsa-param"><span className="pypsa-key">bus_type</span><span className="pypsa-val">{m.pypsa_bus_type}</span></div>
                </div>
              </div>
            )}

            {/* Water grade */}
            {m.water_grade && (
              <div className="def-section">
                <h3>Water Grade</h3>
                <div className="conductor-props-grid">
                  <PropRow label="Grade" value={m.water_grade} highlight />
                </div>
              </div>
            )}

            {m.notes && (
              <div className="def-section">
                <h3>Notes</h3>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>{m.notes}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Main view
  if (loading) return <div className="def-list-view"><div className="def-list-header"><h1>Process Media</h1><p className="def-list-subtitle">Loading...</p></div></div>;

  return (
    <div className="def-list-view">
      <div className="def-list-header">
        <div>
          <h1>Process Media</h1>
          <p className="def-list-subtitle">
            Chemical compounds, water grades, steam classes, and gas mixtures for process modelling.
            <br/><span style={{ color: 'var(--cenec-green)', fontSize: 11 }}>{media.length} media across {categories.length} categories</span>
          </p>
        </div>
        <button className="btn-accent" onClick={startNewMixture}>+ New Gas Mixture</button>
      </div>

      {/* Category filter cards */}
      <div className="conductor-class-grid">
        <button
          className={`conductor-class-card ${!activeCategory ? 'active' : ''}`}
          style={{ borderTopColor: '#4ecca3' }}
          onClick={() => setActiveCategory(null)}
        >
          <div className="conductor-class-count">{media.length}</div>
          <div className="conductor-class-label">All</div>
        </button>
        {Object.entries(CATEGORY_INFO).map(([catId, info]) => {
          const count = categories.find(c => c.category === catId)?.count || 0;
          if (count === 0) return null;
          return (
            <button
              key={catId}
              className={`conductor-class-card ${activeCategory === catId ? 'active' : ''}`}
              style={{ borderTopColor: info.color }}
              onClick={() => setActiveCategory(activeCategory === catId ? null : catId)}
            >
              <div className="conductor-class-volts" style={{ color: info.color }}>{info.icon}</div>
              <div className="conductor-class-count">{count}</div>
              <div className="conductor-class-label">{info.label}</div>
            </button>
          );
        })}
      </div>

      {/* Media table */}
      <div className="conductor-table-wrap">
        <table className="conductor-table">
          <thead>
            <tr>
              <th></th>
              <th>Code</th>
              <th>Name</th>
              <th>Formula</th>
              <th>Category</th>
              <th>Mol Wt</th>
              <th>Density</th>
              <th>LHV</th>
              <th>Phase</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(m => {
              const catInfo = CATEGORY_INFO[m.category] || {};
              return (
                <tr key={m.id} onClick={() => setSelectedMedia(m)} style={{ cursor: 'pointer' }}>
                  <td style={{ fontSize: 16 }}>{m.icon || catInfo.icon}</td>
                  <td className="mono" style={{ color: m.color || catInfo.color, fontWeight: 600 }}>{m.code}</td>
                  <td className="conductor-name">{m.name}</td>
                  <td className="mono">{m.formula || '—'}</td>
                  <td><span className="conductor-vc-badge" style={{ background: catInfo.color || '#666' }}>{catInfo.label || m.category}</span></td>
                  <td className="mono">{m.mol_weight?.toFixed(2) || '—'}</td>
                  <td className="mono">{m.density_kg_m3 || '—'}</td>
                  <td className="mono" style={{ color: m.lhv_mj_per_kg ? '#F59E0B' : 'var(--text-muted)' }}>{m.lhv_mj_per_kg || '—'}</td>
                  <td>{m.is_mixture ? <span className="conductor-type-badge oh">MIX</span> : <span className="conductor-type-badge ug">{m.phase_at_stp || '—'}</span>}</td>
                  <td><button className="btn-card" onClick={e => { e.stopPropagation(); setSelectedMedia(m); }}>View</button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const PropRow = ({ label, value, unit, highlight }) => (
  <div className={`conductor-prop ${highlight ? 'highlight' : ''}`}>
    <span className="conductor-prop-label">{label}</span>
    <span className="conductor-prop-value">
      {value != null ? value : '—'}
      {unit && value != null && <span className="conductor-prop-unit"> {unit}</span>}
    </span>
  </div>
);

export default ProcessMediaView;
